import { dayNumber, formatFullDay, formatTime } from "../format.js";
import { colourOf, hasColour, isNoShowSession, type DiaryEntry } from "./entries.js";
import {
  WEEKDAYS,
  isSameMonth,
  monthOf,
  sessionsByDay,
  type DayKey,
  type Session,
} from "./grid.js";

/**
 * How many lessons a cell shows before it stops counting them out.
 *
 * A month cell is about a centimetre tall. Six lessons in it are unreadable,
 * and an owner's Tuesday can hold thirty — so past this the rest are summed.
 * The number is never wrong, only the list is short.
 */
const SHOWN_PER_DAY = 3;

function schoolLabel(entry: DiaryEntry): string | null {
  if (entry.anonymous || !entry.isSchools) return null;
  const category = entry.categoryName?.trim();
  if (!category) return "School";
  const name = category.replace(/^School\s*·\s*/i, "").trim();
  return name === "" ? "School" : `School · ${name}`;
}

/**
 * A month, for the shape of it.
 *
 * The day number still drills into the detailed day view. A readable lesson,
 * though, is already a concrete appointment: clicking it opens the same action
 * surface as day/week/list instead of making the tutor navigate down a level
 * before they can record payment, move or cancel it. Colleagues' anonymous
 * lessons stay non-actionable and continue to drill into the day.
 */
export function Month({
  anchor,
  entries,
  today,
  onOpenDay,
  onOpenSession,
}: {
  anchor: DayKey;
  entries: DiaryEntry[];
  today: DayKey;
  onOpenDay: (day: DayKey) => void;
  onOpenSession: (session: Session<DiaryEntry>) => void;
}) {
  const days = monthOf(anchor);
  const byDay = sessionsByDay(entries, days);

  return (
    <div className="cal-month">
      {WEEKDAYS.map((weekday) => (
        <div className="cal-month-head" key={weekday}>
          {weekday}
        </div>
      ))}

      {days.map((day) => {
        const sessions = byDay.get(day) ?? [];
        const hidden = sessions.length - SHOWN_PER_DAY;

        const classes = ["cal-cell"];
        if (!isSameMonth(day, anchor)) classes.push("is-outside");
        if (day === today) classes.push("is-today");

        return (
          <div className={classes.join(" ")} key={day}>
            {/* The whole day drills into the detailed day view. The visible day
                number needs the full date in its accessible label so a screen
                reader hears both what is being opened and which day it is. */}
            <button className="cal-cell-day" onClick={() => onOpenDay(day)} type="button">
              <span aria-hidden="true">{dayNumber(day)}</span>
              <span className="visually-hidden">Open {formatFullDay(day)} in day view</span>
            </button>

            {sessions.slice(0, SHOWN_PER_DAY).map((session) => {
              const first = session.bookings[0];
              if (!first) return null;

              // A colleague's lesson is labelled by who is teaching it, which
              // is the only thing about it this account is entitled to. Own
              // lessons are named where there is one person and counted where
              // there are several: three names at this size is a smudge, and
              // "3 students" is at least true.
              const who = first.anonymous
                ? (first.tutorName ?? "No tutor recorded")
                : session.bookings.length > 1
                  ? `${session.bookings.length} students`
                  : (first.studentName ?? first.customerName ?? "Unknown");
              const school = schoolLabel(first);
              const label = school === null ? who : `${who} · ${school}`;
              const noShow = isNoShowSession(session);
              const title = `${formatTime(session.startsAt)} · ${label}${noShow ? " · no-show" : ""}`;

              return (
                <button
                  className={`cal-entry${first.anonymous ? " is-other" : ""}${noShow ? " is-no-show" : ""}${
                    hasColour(first) ? " has-colour" : ""
                  }`}
                  key={session.appointmentId}
                  onClick={() => first.anonymous ? onOpenDay(day) : onOpenSession(session)}
                  style={colourOf(first)}
                  title={title}
                  aria-label={title}
                  type="button"
                >
                  {noShow && <span aria-hidden="true">⊘ </span>}
                  <time dateTime={session.startsAt}>{formatTime(session.startsAt)}</time> {label}
                </button>
              );
            })}

            {hidden > 0 && (
              <button className="cal-more" onClick={() => onOpenDay(day)} type="button">
                +{hidden} more
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
